import { useCallback, useMemo } from 'react';

import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { usePlayerProfileDataStore } from '@/feature/player-profile-sheets/hooks/use-player-profile-data-store';
import { RootStackParamList } from '@/navigation/types';

export const useOpenPlayerProfile = (players: PlayerWithTeamAndEvent[]) => {
    // Ensure unique players based on player ID and event ID combination
    const uniquePlayers = useMemo(() => {
        const uniquePlayerSet = new Set<string>();
        return players.filter(player => {
            const uniqueKey = `${player.id}-${player.event.id}`;
            if (uniquePlayerSet.has(uniqueKey)) {
                return false;
            }
            uniquePlayerSet.add(uniqueKey);
            return true;
        });
    }, [players]);
    const setPlayersList = usePlayerProfileDataStore(state => state.actions.setPlayersList);
    const setSelectedPlayerIndex = usePlayerProfileDataStore(state => state.actions.setSelectedPlayerIndex);
    const setSelectedPlayerProjectionFilter = usePlayerProfileDataStore(
        state => state.actions.setSelectedPlayerProjectionFilter
    );
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const openPlayerProfile = useCallback(
        ({
            playerId,
            eventId,
            playerProjectionFilter,
        }: {
            playerId: string;
            eventId: string;
            playerProjectionFilter?: string;
        }) => {
            const index = uniquePlayers.findIndex(player => player.id === playerId && player.event.id === eventId) ?? 0;
            setSelectedPlayerProjectionFilter(playerProjectionFilter);
            setSelectedPlayerIndex(index || 0);
            setPlayersList(uniquePlayers || []);
            navigation.navigate('PlayerProfileSheets', {
                contextKey: new Date().getTime().toString(),
            });
        },
        [navigation, uniquePlayers, setPlayersList, setSelectedPlayerIndex, setSelectedPlayerProjectionFilter]
    );

    return {
        openPlayerProfile,
    };
};
